import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from "@angular/core";
import { FormArray, FormBuilder, FormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { ICredentials, ILoggedInUser } from "@gloria-public/login/domain/interfaces/login.interface";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "@shared/services/auth.service";
import { ConnectionEventsService } from "@shared/services/connection-events.service";
import { PREVENT_CHARACTER } from "@shared/utils/helpers/prevent-character-helper";
import { Subject, finalize, first, takeUntil } from "rxjs";
import { ResendVerifyComponent } from "../resend-verify/resend-verify.component";

@Component({
  selector: "app-login-form",
  templateUrl: "./login-form.component.html",
  styleUrls: ["./login-form.component.scss"],
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @ViewChildren("digitInput") private digitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  @Input() isModal = false;
  @Input() title!: string;
  @Output() loginFormSubmit: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() logoutClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() forgotPasswordClick: EventEmitter<void> = new EventEmitter<void>();
  show2FA = false;
  isLoginFailed = false;
  is2FAFailed = false;

  public loginForm = this.fb.nonNullable.group({
    login: ["", [Validators.required, Validators.maxLength(50)]],
    password: ["", [Validators.required, Validators.maxLength(255)]],
  });

  public digits2FAForm = this.fb.nonNullable.group({
    digits: this.fb.array([]),
  });
  public nbDigit = 6;
  public PREVENT_CHARACTER = PREVENT_CHARACTER;
  public isUserVerified = true;
  public showPassword = false;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private connectionEventsService: ConnectionEventsService
  ) {}

  /**
   * Initializes the 2FA form.
   */
  ngOnInit(): void {
    this.init2FAForm();
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Returns 2FA digits form array
   * @return FormArray containing digit controls
   */
  public get digitsFormArray(): FormArray {
    return this.digits2FAForm.get("digits") as FormArray;
  }

  /**
   * Initializes 2FA form controls and listeners
   */
  public init2FAForm(): void {
    for (let i = 0; i < this.nbDigit; i++) {
      this.digitsFormArray.push(this.fb.control("", Validators.required));
      if (i < this.nbDigit - 1) {
        (this.digitsFormArray.at(i) as FormControl).valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
          if (value && value !== "" && !isNaN(value)) {
            // focus on next digit input
            document.getElementById("digitInput" + (i + 1))?.focus();
          }
        });
      }
    }
  }

  /**
   * Resets all 2FA input fields
   */
  public reset2FAForm(): void {
    this.digitsFormArray.reset();

    setTimeout(() => this.focusInput(0));
  }

  /**
   * Builds 2FA code string from form values
   * @return Concatenated 2FA code string
   */
  public get2FAStringFromForm(): string {
    const digits = this.digitsFormArray.getRawValue() as number[];
    let code2FAString = "";
    digits.forEach((v) => {
      code2FAString += v;
    });
    return code2FAString;
  }

  /**
   * Handles digit input, keeps only numeric characters, and advances focus.
   * @param event Input event triggered by the edited field.
   * @param index Index of the current digit input.
   */
  public onDigitInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;

    // Keep only digits
    const value = input.value.replace(/\D/g, "");

    this.digitsFormArray.at(index).setValue(value);

    if (value && index < this.digitsFormArray.length - 1) {
      this.focusInput(index + 1);
    }
  }

  /**
   * Handles keyboard navigation and deletion within the 2FA inputs.
   * @param event Keyboard event triggered by the current input.
   * @param index Index of the current digit input.
   */
  public onKeyDown(event: KeyboardEvent, index: number): void {
    const control = this.digitsFormArray.at(index);

    switch (event.key) {
      case "ArrowLeft":
        if (index > 0) {
          this.focusInput(index - 1);
        }
        event.preventDefault();
        return;

      case "ArrowRight":
        if (index < this.digitsFormArray.length - 1) {
          this.focusInput(index + 1);
        }
        event.preventDefault();
        return;

      case "Backspace":
        if (control.value) {
          control.setValue("");
        } else if (index > 0) {
          this.digitsFormArray.at(index - 1).setValue("");
          this.focusInput(index - 1);
        }

        event.preventDefault();
        return;
    }
  }

  /**
   * Moves focus to the specified 2FA input and selects its content.
   * @param index Index of the input to focus.
   */
  private focusInput(index: number): void {
    const input = this.digitInputs.get(index);

    input?.nativeElement.focus();
    input?.nativeElement.select();
  }

  /**
   * Handles back navigation
   */
  public goBack(): void {
    if (this.show2FA) {
      this.show2FA = false;
      this.reset2FAForm();
      this.digits2FAForm.markAsUntouched();
      this.is2FAFailed = false;
    } else {
      this.router.navigate(["/landing"]);
    }
  }

  /**
   * Distributes pasted digits across the 2FA inputs starting from the current field.
   * @param event Clipboard event containing the pasted content.
   * @param startIndex Index of the input where the paste occurred.
   */
  public pasteEvent(event: ClipboardEvent, startIndex: number): void {
    event.preventDefault();

    const pasted = event.clipboardData?.getData("text") ?? "";
    const digits = pasted.replace(/\D/g, "");

    digits
      .split("")
      .slice(0, this.digitsFormArray.length - startIndex)
      .forEach((digit, offset) => {
        this.digitsFormArray.at(startIndex + offset).setValue(digit);
      });

    const lastIndex = Math.min(startIndex + digits.length - 1, this.digitsFormArray.length - 1);
    this.focusInput(lastIndex);
  }

  /**
   * Handles login or 2FA submission flow
   * @param resendCode Indicates if code should be resent
   */
  public onSubmit(resendCode = false): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.show2FA = false;
      return;
    }
    if (!this.show2FA || resendCode) {
      this.loginForm.disable();
      this.login({
        login: this.loginForm.controls.login.value,
        password: this.loginForm.controls.password.value,
      });
    } else {
      if (this.digits2FAForm.invalid) {
        this.digits2FAForm.markAllAsTouched();
        return;
      }
      this.loginForm.disable();
      this.digits2FAForm.disable();
      this.login(
        {
          login: this.loginForm.controls.login.value,
          password: this.loginForm.controls.password.value,
        },
        this.get2FAStringFromForm()
      );
      this.reset2FAForm();
      this.digits2FAForm.markAsUntouched();
    }
  }

  /**
   * Performs login request with given credentials
   * @param credentials User login credentials
   * @param code2FA code used for the 2FA
   */
  private login(credentials: ICredentials, code2FA?: string): void {
    this.authService
      .login(credentials, code2FA)
      .pipe(
        first(),
        finalize(() => {
          this.loginForm.enable();
          this.digits2FAForm.enable();
        })
      )
      .subscribe({
        next: (value: ILoggedInUser) => {
          if (value.tokens.accessToken && value.tokens.refreshToken) {
            this.translateService.use(value.language);
            this.connectionEventsService.notifyLogin(value.tokens, value.language);
            this.loginFormSubmit.emit(true);
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
            throw new Error("loginFormSubmit, no accessToken received");
          }
        },
        error: (err) => {
          if (err.status === 400) {
            this.isLoginFailed = true;
            this.isUserVerified = true;
            this.show2FA = false;
            this.is2FAFailed = false;
          } else if (err.status === 401) {
            this.isLoginFailed = false;
            this.isUserVerified = true;
            this.show2FA = true;
          } else if (err.status === 403 && err.error === "not_verified") {
            this.isLoginFailed = false;
            this.isUserVerified = false;
            this.show2FA = false;
            this.is2FAFailed = false;
          } else if (err.status === 403 && err.error === "invalid_totp") {
            this.isLoginFailed = false;
            this.isUserVerified = true;
            this.is2FAFailed = true;
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
          }
        },
      });
  }

  /**
   * Handles forgot password action
   * @param event Mouse event triggering action
   */
  public onForgotPasswordClick(event: MouseEvent): void {
    if (this.isModal) {
      event.preventDefault();
      this.forgotPasswordClick.emit();
    } else {
      this.router.navigate(["/reset-password"]);
    }
  }

  /**
   * Opens resend verification dialog
   */
  public resend(): void {
    this.dialog.open(ResendVerifyComponent, {
      maxWidth: "100vw",
      maxHeight: "100vh",
      data: { isModal: true },
    });
  }

  /**
   * Logs out user and redirects
   * @param event Mouse event triggering logout
   */
  public logout(event: MouseEvent): void {
    event.preventDefault();
    this.logoutClick.emit();
    this.connectionEventsService.notifyLogout();
  }
}
